/**
 * CodeSkin PDP — gallery, proof scroller, variant/size picker, price sync
 */
(function () {
  function initPdp(root) {
    if (!root || root.dataset.pdpBound === 'true') return;
    root.dataset.pdpBound = 'true';

    initGallery(root);
    initProofScroller(root);
    initVariants(root);
    initPdpCart(root);
    initFbt(root);
    initMobileScrollStrips(root);
    initVtVideos(root);
    initReviews(root);
    initSectionNav(root);
    initPincode(root);
    initTrustTaps(root);
    initAiScanVideos(root);
    initElevator(root);
    initShareButton(root);
  }

  function initShareButton(root) {
    root.querySelectorAll('[data-pdp-share]').forEach(function (btn) {
      if (btn.dataset.shareBound === 'true') return;
      btn.dataset.shareBound = 'true';

      function showFeedback(text) {
        var original = btn.getAttribute('aria-label');
        btn.setAttribute('aria-label', text);
        window.setTimeout(function () {
          btn.setAttribute('aria-label', original);
        }, 1600);
      }

      function copyToClipboard(url) {
        if (navigator.clipboard && navigator.clipboard.writeText) {
          navigator.clipboard.writeText(url).then(function () {
            showFeedback('Link copied');
          }).catch(function () {
            showFeedback('Could not copy link');
          });
          return;
        }
        var input = document.createElement('input');
        input.value = url;
        input.style.position = 'fixed';
        input.style.opacity = '0';
        document.body.appendChild(input);
        input.select();
        try {
          document.execCommand('copy');
          showFeedback('Link copied');
        } catch (e) {
          showFeedback('Could not copy link');
        }
        document.body.removeChild(input);
      }

      btn.addEventListener('click', function () {
        var title = btn.getAttribute('data-share-title') || document.title;
        var url = btn.getAttribute('data-share-url') || window.location.href;

        if (navigator.share) {
          navigator.share({ title: title, url: url }).catch(function () { /* user cancelled */ });
          return;
        }
        copyToClipboard(url);
      });
    });
  }

  function initElevator(root) {
    root.querySelectorAll('[data-codeskin-elevator]').forEach(function (wrap) {
      if (wrap.dataset.elevatorBound === 'true') return;
      wrap.dataset.elevatorBound = 'true';

      /*
       * Product descriptions occasionally arrive from Shopify with a literal
       * arrow inside rich-text markup. The PDP stylesheet supplies one arrow
       * for every bullet, so remove any leading content arrow to avoid a
       * duplicate. A TreeWalker handles arrows placed inside spans/strong tags.
       */
      wrap.querySelectorAll('.elevator-bullets li, .elevator-body li').forEach(function (item) {
        var walker = document.createTreeWalker(item, NodeFilter.SHOW_TEXT);
        var textNode;

        while ((textNode = walker.nextNode())) {
          if (!textNode.nodeValue.trim()) continue;

          var originalValue = textNode.nodeValue;
          var cleanedValue = originalValue.replace(
            /^\s*(?:-+\s*(?:>|&gt;)|→|&rarr;)\s*/,
            ''
          );

          if (cleanedValue !== originalValue) {
            textNode.nodeValue = cleanedValue;
            continue;
          }

          /*
           * Rich text can split "->" across separate spans/text nodes.
           * Remove those standalone fragments, then continue to the first
           * actual content node.
           */
          if (/^\s*(?:-+|>|&gt;|→|&rarr;)\s*$/.test(originalValue)) {
            textNode.nodeValue = '';
            continue;
          }

          break;
        }
      });

      var toggle = wrap.querySelector('[data-codeskin-elevator-toggle]');
      var full = wrap.querySelector('[data-codeskin-elevator-full]');
      var body = wrap.querySelector('[data-codeskin-elevator-body]');
      if (!toggle) return;

      toggle.addEventListener('click', function () {
        var expanded = wrap.classList.toggle('is-expanded');
        toggle.setAttribute('aria-expanded', expanded ? 'true' : 'false');

        if (full) {
          full.hidden = !expanded;
        }

        var label = toggle.querySelector('[data-elevator-toggle-label]');
        var arrow = toggle.querySelector('.elevator-read-more-arrow');
        if (label) {
          label.textContent = expanded ? 'Show less' : 'Show more';
        }
        if (arrow) {
          arrow.textContent = expanded ? '↑' : '↓';
        }
      });

      if (body && !body.querySelector('ul, ol')) {
        wrap.classList.add('elevator--plain');
      }
    });
  }

  function initAiScanVideos(root) {
    root.querySelectorAll('.codeskin-ai-scan-video').forEach(function (video) {
      if (video.dataset.aiScanVideoBound === 'true') return;
      video.dataset.aiScanVideoBound = 'true';
      video.muted = true;
      function kickPlay() {
        var p = video.play();
        if (p && typeof p.catch === 'function') p.catch(function () {});
      }
      kickPlay();
      video.addEventListener('loadeddata', kickPlay, { passive: true });
    });
  }

  function initGallery(root) {
    var gallery = root.querySelector('[data-codeskin-gallery]');
    if (!gallery) return;

    var stage = gallery.querySelector('[data-codeskin-stage]');
    var stageImg = gallery.querySelector('[data-codeskin-stage-img]');
    var lens = gallery.querySelector('[data-codeskin-lens]');
    var zoom = gallery.querySelector('[data-codeskin-zoom]');
    var main = gallery.querySelector('.gallery-main');
    var thumbs = gallery.querySelectorAll('[data-codeskin-thumb]');
    if (!stage || !stageImg) return;

    var activeSrc = stageImg.getAttribute('src') || '';
    var zoomFactor = 2.4;

    function selectThumb(thumb) {
      thumbs.forEach(function (t) {
        t.classList.remove('active');
        t.setAttribute('aria-selected', 'false');
      });
      thumb.classList.add('active');
      thumb.setAttribute('aria-selected', 'true');

      if (thumb.getAttribute('data-mode') === 'proof') {
        stage.classList.add('proof-mode');
        return;
      }
      stage.classList.remove('proof-mode');

      var src = thumb.getAttribute('data-img');
      if (!src || src === activeSrc) return;

      stage.classList.add('is-loading');
      var probe = new Image();
      probe.onload = function () {
        stageImg.src = src;
        activeSrc = src;
        if (zoom) zoom.style.backgroundImage = 'url("' + src + '")';
        stage.classList.remove('is-loading');
      };
      probe.onerror = function () {
        stage.classList.remove('is-loading');
      };
      probe.src = src;
    }

    thumbs.forEach(function (thumb) {
      thumb.addEventListener('click', function () {
        selectThumb(thumb);
      });
      var hoverTimer;
      thumb.addEventListener('mouseenter', function () {
        hoverTimer = window.setTimeout(function () {
          selectThumb(thumb);
        }, 120);
      });
      thumb.addEventListener('mouseleave', function () {
        if (hoverTimer) window.clearTimeout(hoverTimer);
      });
    });

    var supportsHover = window.matchMedia('(hover: hover) and (min-width: 981px)').matches;
    if (!supportsHover || !lens || !zoom) return;

    function onEnter() {
      if (stage.classList.contains('proof-mode')) return;
      zoom.style.backgroundImage = 'url("' + activeSrc + '")';
      stage.classList.add('zooming');
      if (main) main.classList.add('zooming');
    }

    function onLeave() {
      stage.classList.remove('zooming');
      if (main) main.classList.remove('zooming');
    }

    function onMove(e) {
      if (stage.classList.contains('proof-mode')) return;
      var rect = stage.getBoundingClientRect();
      var x = e.clientX - rect.left;
      var y = e.clientY - rect.top;
      var lensW = lens.offsetWidth;
      var lensH = lens.offsetHeight;
      var lx = Math.max(0, Math.min(x - lensW / 2, rect.width - lensW));
      var ly = Math.max(0, Math.min(y - lensH / 2, rect.height - lensH));
      lens.style.left = lx + 'px';
      lens.style.top = ly + 'px';

      var ratioX = rect.width > lensW ? lx / (rect.width - lensW) : 0;
      var ratioY = rect.height > lensH ? ly / (rect.height - lensH) : 0;
      var bgW = rect.width * zoomFactor;
      var bgH = rect.height * zoomFactor;
      zoom.style.backgroundSize = bgW + 'px ' + bgH + 'px';
      var zw = zoom.offsetWidth;
      var zh = zoom.offsetHeight;
      zoom.style.backgroundPosition = -(ratioX * (bgW - zw)) + 'px ' + -(ratioY * (bgH - zh)) + 'px';
    }

    stage.addEventListener('mouseenter', onEnter);
    stage.addEventListener('mouseleave', onLeave);
    stage.addEventListener('mousemove', onMove);
  }

  function initProofScroller(root) {
    var scroller = root.querySelector('[data-codeskin-proof]');
    if (!scroller) return;

    var track = scroller.querySelector('.proof-track');
    var slides = scroller.querySelectorAll('.proof-slide');
    var prev = scroller.querySelector('[data-proof-prev]');
    var next = scroller.querySelector('[data-proof-next]');
    var dotsEl = scroller.querySelector('[data-proof-dots]');
    if (!track || !slides.length) return;

    if (dotsEl) {
      dotsEl.innerHTML = '';
      slides.forEach(function (_, i) {
        var dot = document.createElement('button');
        dot.type = 'button';
        dot.setAttribute('aria-label', 'Report ' + (i + 1));
        if (i === 0) dot.setAttribute('aria-current', 'true');
        dot.addEventListener('click', function () {
          slides[i].scrollIntoView({ behavior: 'smooth', inline: 'center', block: 'nearest' });
        });
        dotsEl.appendChild(dot);
      });
    }

    function syncDots() {
      if (!dotsEl) return;
      var idx = 0;
      var trackRect = track.getBoundingClientRect();
      var center = trackRect.left + trackRect.width / 2;
      slides.forEach(function (slide, i) {
        var r = slide.getBoundingClientRect();
        if (r.left <= center && r.right >= center) idx = i;
      });
      dotsEl.querySelectorAll('button').forEach(function (btn, i) {
        btn.setAttribute('aria-current', i === idx ? 'true' : 'false');
      });
    }

    function scrollBy(dir) {
      var w = slides[0].offsetWidth + 20;
      track.scrollBy({ left: dir * w, behavior: 'smooth' });
    }

    if (prev) prev.addEventListener('click', function () { scrollBy(-1); });
    if (next) next.addEventListener('click', function () { scrollBy(1); });
    track.addEventListener('scroll', syncDots, { passive: true });

    var lightbox = root.querySelector('[data-proof-lightbox]');
    var lightboxImg = lightbox ? lightbox.querySelector('[data-proof-lightbox-img]') : null;
    var lightboxClose = lightbox ? lightbox.querySelector('[data-proof-lightbox-close]') : null;

    function openLightbox(fullSrc, fullAlt) {
      if (!lightbox || !lightboxImg || !fullSrc) return;
      lightboxImg.src = fullSrc;
      lightboxImg.alt = fullAlt || '';
      lightbox.hidden = false;
      document.body.classList.add('overflow-hidden');
    }

    function closeLightbox() {
      if (!lightbox) return;
      lightbox.hidden = true;
      lightboxImg.src = '';
      document.body.classList.remove('overflow-hidden');
    }

    scroller.querySelectorAll('[data-proof-zoom]').forEach(function (btn) {
      btn.addEventListener('click', function () {
        openLightbox(btn.getAttribute('data-full-src'), btn.getAttribute('data-full-alt'));
      });
    });

    if (lightboxClose) lightboxClose.addEventListener('click', closeLightbox);
    if (lightbox) {
      lightbox.addEventListener('click', function (e) {
        if (e.target === lightbox) closeLightbox();
      });
    }
    document.addEventListener('keydown', function (e) {
      if (e.key === 'Escape' && lightbox && !lightbox.hidden) closeLightbox();
    });
  }

  function initVariants(root) {
    var dataEl = root.querySelector('[data-codeskin-variants-json]');
    var idInput = root.querySelector('[data-codeskin-variant-id]');
    var priceEl = root.querySelector('[data-codeskin-price]');
    var compareEl = root.querySelector('[data-codeskin-compare-price]');
    var atcLabel = root.querySelector('[data-codeskin-atc-label]');
    var sizeBtns = root.querySelectorAll('[data-codeskin-size-opt]');
    if (!dataEl || !idInput) return;

    var product;
    try {
      product = JSON.parse(dataEl.textContent);
    } catch (e) {
      return;
    }

    var moneyFormat = root.getAttribute('data-money-format') || '{{amount}}';

    function formatMoney(cents) {
      if (window.Shopify && typeof window.Shopify.formatMoney === 'function') {
        return window.Shopify.formatMoney(cents, moneyFormat);
      }
      return '₹' + (cents / 100).toFixed(0);
    }

    function applyVariant(variant) {
      if (!variant) return;
      idInput.value = variant.id;
      idInput.disabled = false;
      if (priceEl) priceEl.textContent = formatMoney(variant.price);
      if (compareEl) {
        if (variant.compare_at_price && variant.compare_at_price > variant.price) {
          compareEl.textContent = formatMoney(variant.compare_at_price);
          compareEl.hidden = false;
        } else {
          compareEl.hidden = true;
        }
      }
      var atcText = variant.available ? 'Add to Cart' : 'Sold out';
      if (atcLabel) {
        atcLabel.textContent = atcText;
      }
      if (typeof root._codeskinPdpSetDefaultLabel === 'function') {
        root._codeskinPdpSetDefaultLabel(atcText);
      }
      var submit = root.querySelector('[data-codeskin-atc-submit]');
      if (submit) {
        submit.disabled = !variant.available;
        submit.classList.remove('is-added');
        submit.removeAttribute('aria-disabled');
      }
      if (typeof root._codeskinPdpCartSync === 'function') {
        root._codeskinPdpCartSync();
      }
      // Static gallery with a second image set for one size (see codeskin-pdp-gallery.liquid).
      var sizeGallery = root.querySelector('[data-codeskin-gallery][data-variant-gallery-key]');
      if (sizeGallery) {
        var sizeKey = String(variant.title || '').toLowerCase().replace(/\s+/g, '');
        var listAttr = sizeKey === sizeGallery.getAttribute('data-variant-gallery-key') ? 'data-variant-gallery' : 'data-default-gallery';
        var sizeImgs = (sizeGallery.getAttribute(listAttr) || '').split('||').filter(Boolean);
        var sizeThumbs = sizeGallery.querySelectorAll('[data-codeskin-thumb]:not([data-mode="proof"])');
        if (sizeImgs.length && sizeThumbs.length && sizeThumbs[0].getAttribute('data-img') !== sizeImgs[0]) {
          sizeThumbs.forEach(function (t, i) {
            if (!sizeImgs[i]) {
              t.hidden = true;
              return;
            }
            t.hidden = false;
            t.setAttribute('data-img', sizeImgs[i]);
            var tImg = t.querySelector('img');
            if (tImg) tImg.src = sizeImgs[i];
          });
          var startIdx = parseInt(sizeGallery.getAttribute('data-default-index'), 10) || 0;
          var startThumb = sizeThumbs[startIdx] && !sizeThumbs[startIdx].hidden ? sizeThumbs[startIdx] : sizeThumbs[0];
          startThumb.click();
        }
      }
      if (variant.featured_image && variant.featured_image.src) {
        var stageImg = root.querySelector('[data-codeskin-stage-img]');
        if (stageImg && stageImg.getAttribute('data-static-stage') !== 'true') {
          var activeThumb = root.querySelector('[data-codeskin-thumb].active');
          if (!activeThumb || activeThumb.getAttribute('data-mode') !== 'proof') {
            stageImg.src = variant.featured_image.src;
            var firstThumb = root.querySelector('[data-codeskin-thumb]:not([data-mode="proof"])');
            if (firstThumb) {
              firstThumb.setAttribute('data-img', variant.featured_image.src);
              var thumbImg = firstThumb.querySelector('img');
              if (thumbImg) thumbImg.src = variant.featured_image.src;
            }
          }
        }
      }
      var url = new URL(window.location.href);
      url.searchParams.set('variant', variant.id);
      window.history.replaceState({}, '', url);
    }

    function findVariant(opts) {
      return product.variants.find(function (v) {
        if (opts[0] && v.option1 !== opts[0]) return false;
        if (opts[1] && v.option2 !== opts[1]) return false;
        if (opts[2] && v.option3 !== opts[2]) return false;
        return true;
      });
    }

    function variantOptions(v) {
      return [v.option1, v.option2, v.option3].filter(function (o) {
        return o != null && o !== '';
      });
    }

    var selected = variantOptions(product.variants[0]);

    sizeBtns.forEach(function (btn) {
      btn.addEventListener('click', function () {
        if (btn.classList.contains('oos') || btn.getAttribute('aria-disabled') === 'true') return;
        var idx = parseInt(btn.getAttribute('data-option-index'), 10);
        var val = btn.getAttribute('data-option-value');
        selected[idx] = val;
        sizeBtns.forEach(function (b) {
          if (parseInt(b.getAttribute('data-option-index'), 10) === idx) {
            b.classList.toggle('active', b === btn);
            b.setAttribute('aria-checked', b === btn ? 'true' : 'false');
          }
        });
        applyVariant(findVariant(selected));
      });
    });

    var params = new URLSearchParams(window.location.search);
    var vid = params.get('variant');
    if (vid) {
      var fromUrl = product.variants.find(function (v) {
        return String(v.id) === String(vid);
      });
      if (fromUrl) {
        selected = variantOptions(fromUrl);
        applyVariant(fromUrl);
        sizeBtns.forEach(function (btn) {
          var idx = parseInt(btn.getAttribute('data-option-index'), 10);
          var val = btn.getAttribute('data-option-value');
          var fromOpts = variantOptions(fromUrl);
          var match = fromOpts[idx] === val;
          btn.classList.toggle('active', match);
          btn.setAttribute('aria-checked', match ? 'true' : 'false');
        });
        return;
      }
    }
    applyVariant(product.variants.find(function (v) { return v.available; }) || product.variants[0]);
  }

  function initPdpCart(root) {
    var form = root.querySelector('.codeskin-pdp-form');
    if (!form || form.dataset.pdpCartBound === 'true') return;
    form.dataset.pdpCartBound = 'true';

    var wrap = form.querySelector('[data-codeskin-qty]');
    var input = wrap && wrap.querySelector('input[name="quantity"]');
    var minus = wrap && wrap.querySelector('[data-qty-minus]');
    var plus = wrap && wrap.querySelector('[data-qty-plus]');
    var submit = form.querySelector('[data-codeskin-atc-submit]');
    var label = form.querySelector('[data-codeskin-atc-label]');
    var variantInput = form.querySelector('[data-codeskin-variant-id]');
    if (!submit && !input) return;

    var desktopMq = window.matchMedia('(min-width: 901px)');
    var defaultLabel = label ? label.textContent.trim() : 'Add to Cart';
    var addedLabel = 'Added to Cart';
    var lineKey = null;
    var qtyPending = false;
    var addPending = false;

    function getCartChangeUrl() {
      if (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) {
        return window.Shopify.routes.root + 'cart/change.js';
      }
      return '/cart/change.js';
    }

    function getCartJsUrl() {
      var base = (window.routes && window.routes.cart_url) || '/cart';
      return base.indexOf('.js') === base.length - 3 ? base : base.replace(/\/$/, '') + '.js';
    }

    function getCartAddUrl() {
      var url = (window.routes && window.routes.cart_add_url) || '/cart/add';
      if (url.indexOf('.js') === url.length - 3) return url;
      return url.replace(/\/$/, '') + '.js';
    }

    function getVariantId() {
      return variantInput && variantInput.value ? String(variantInput.value) : '';
    }

    function readQty() {
      if (!input) return 1;
      var n = parseInt(input.value, 10);
      return isNaN(n) || n < 1 ? 1 : n;
    }

    function writeQty(n) {
      if (!input) return;
      input.value = String(Math.max(1, n));
    }

    function setAddedState(inCart, options) {
      if (!submit || !label) return;
      options = options || {};
      if (inCart) {
        submit.classList.add('is-added');
        label.textContent = addedLabel;
        submit.setAttribute('aria-label', addedLabel);
        if (options.temporary) {
          submit.removeAttribute('aria-disabled');
        } else {
          submit.setAttribute('aria-disabled', 'true');
        }
      } else {
        submit.classList.remove('is-added');
        submit.removeAttribute('aria-disabled');
        submit.removeAttribute('aria-label');
        if (label.textContent === addedLabel) {
          label.textContent = defaultLabel;
        }
      }
    }

    function findLine(cart) {
      var vid = getVariantId();
      if (!cart || !vid || !cart.items) return null;
      for (var i = 0; i < cart.items.length; i++) {
        if (String(cart.items[i].variant_id) === vid) return cart.items[i];
      }
      return null;
    }

    function syncFromCart(cart) {
      var line = findLine(cart);
      var inCart = !!(line && line.quantity > 0);

      if (wrap) wrap.hidden = !inCart;
      form.classList.toggle('has-cart-line', inCart);

      if (inCart) {
        lineKey = line.key;
        setAddedState(true, { temporary: true });
      } else {
        lineKey = null;
        setAddedState(false);
      }
      writeQty(readQty());
    }

    function fetchCart() {
      return fetch(getCartJsUrl(), { credentials: 'same-origin' })
        .then(function (r) { return r.json(); })
        .catch(function () { return null; });
    }

    function changeLineQuantity(key, quantity) {
      return fetch(getCartChangeUrl(), {
        method: 'POST',
        credentials: 'same-origin',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json',
          'X-Requested-With': 'XMLHttpRequest',
        },
        body: JSON.stringify({ id: key, quantity: quantity }),
      }).then(function (response) {
        return response.json().then(function (data) {
          if (!response.ok || data.status) {
            throw new Error(data.description || data.message || 'Could not update cart');
          }
          return data;
        });
      });
    }

    function setQtyControlsDisabled(disabled) {
      if (minus) minus.disabled = !!disabled;
      if (plus) plus.disabled = !!disabled;
    }

    function pushCartQty(quantity) {
      if (!lineKey || qtyPending) return;
      qtyPending = true;
      setQtyControlsDisabled(true);

      changeLineQuantity(lineKey, quantity)
        .then(function (cart) {
          syncFromCart(cart);
          document.dispatchEvent(new CustomEvent('cart:refresh', { bubbles: true }));
          if (typeof publish === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
            publish(PUB_SUB_EVENTS.cartUpdate, {
              source: 'codeskin-pdp',
              productVariantId: getVariantId(),
              cartData: cart,
            });
          }
        })
        .catch(function () {
          return fetchCart().then(syncFromCart);
        })
        .finally(function () {
          qtyPending = false;
          setQtyControlsDisabled(false);
        });
    }

    function adjustQty(delta) {
      if (qtyPending) return;

      var current = readQty();
      var next = current + delta;

      if (!lineKey) {
        writeQty(Math.max(1, next));
        return;
      }

      if (next < 0) next = 0;
      if (next > 0) writeQty(next);
      pushCartQty(next);
    }

    if (minus) {
      minus.addEventListener('click', function (e) {
        e.preventDefault();
        e.stopPropagation();
        adjustQty(-1);
      });
    }
    if (plus) {
      plus.addEventListener('click', function (e) {
        e.preventDefault();
        e.stopPropagation();
        adjustQty(1);
      });
    }

    if (input) {
      input.addEventListener('change', function () {
        var next = readQty();
        writeQty(next);
        if (lineKey) pushCartQty(next);
      });
    }

    root._codeskinPdpCartSync = function () {
      fetchCart().then(syncFromCart);
    };

    root._codeskinPdpSetDefaultLabel = function (text) {
      defaultLabel = text || 'Add to Cart';
    };

    if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
      subscribe(PUB_SUB_EVENTS.cartUpdate, function (event) {
        if (event && event.source === 'codeskin-pdp') return;
        fetchCart().then(syncFromCart);
      });
    }

    function addToCartFromPdp() {
      if (!submit || submit.disabled || addPending || qtyPending) return Promise.resolve(null);
      if (submit.classList.contains('is-added') && submit.getAttribute('aria-disabled') === 'true') {
        return Promise.resolve(null);
      }

      var variantId = getVariantId();
      if (!variantId) return Promise.resolve(null);

      addPending = true;
      submit.setAttribute('aria-disabled', 'true');
      submit.classList.add('is-adding');

      var fd = new FormData();
      fd.append('id', variantId);
      fd.append('quantity', String(readQty()));

      return fetch(getCartAddUrl(), {
        method: 'POST',
        credentials: 'same-origin',
        body: fd,
        headers: { 'X-Requested-With': 'XMLHttpRequest', Accept: 'application/json' },
      })
        .then(function (response) {
          return response.json().then(function (data) {
            if (!response.ok || data.status) {
              throw new Error(data.description || data.message || 'Add to cart failed');
            }
            return data;
          });
        })
        .then(function () {
          return fetchCart();
        })
        .then(function (cart) {
          syncFromCart(cart);
          setAddedState(true, {});
          document.dispatchEvent(new CustomEvent('cart:refresh', { bubbles: true }));
          if (typeof window.showCodeskinCartToast === 'function') {
            window.showCodeskinCartToast(
              (window.codeskinCartStrings && window.codeskinCartStrings.added) || 'Added to your bag',
              'success'
            );
          }
          if (typeof fetchAjaxCartState === 'function' && typeof refreshAjaxCartUI === 'function') {
            fetchAjaxCartState()
              .then(function (cartData) {
                refreshAjaxCartUI(cartData);
              })
              .catch(function () { /* ignore */ });
          }
          if (typeof publish === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
            publish(PUB_SUB_EVENTS.cartUpdate, {
              source: 'codeskin-pdp',
              productVariantId: variantId,
              cartData: cart,
            });
          }
          return cart;
        })
        .catch(function (err) {
          submit.removeAttribute('aria-disabled');
          submit.classList.remove('is-added', 'is-adding');
          if (typeof window.showCodeskinCartToast === 'function') {
            window.showCodeskinCartToast(err.message || 'Could not add to bag', 'error');
          }
          return fetchCart().then(syncFromCart);
        })
        .finally(function () {
          addPending = false;
          submit.classList.remove('is-adding');
        });
    }

    document.addEventListener('cart:refresh', function () {
      fetchCart().then(syncFromCart);
    });

    // Hard-block native /cart/add navigation. ATC is AJAX-only on PDP.
    form.setAttribute('action', 'javascript:void(0)');
    form.addEventListener('submit', function (event) {
      event.preventDefault();
      event.stopPropagation();
      event.stopImmediatePropagation();
      return false;
    }, true);

    if (submit) {
      submit.addEventListener('click', function (event) {
        event.preventDefault();
        event.stopPropagation();
        event.stopImmediatePropagation();
        addToCartFromPdp();
      }, true);
    }

    if (typeof desktopMq.addEventListener === 'function') {
      desktopMq.addEventListener('change', function () {
        fetchCart().then(syncFromCart);
      });
    }

    writeQty(1);
    fetchCart().then(syncFromCart);
  }

  function initMobileScrollStrips(root) {
    var mq = window.matchMedia('(max-width: 900px)');
    var reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
    var strips = root.querySelectorAll('[data-codeskin-scroll-strip]');

    strips.forEach(function (viewport) {
      if (viewport.dataset.scrollStripBound === 'true') return;
      viewport.dataset.scrollStripBound = 'true';

      var track = viewport.querySelector('[data-scroll-strip-inner]');
      if (!track) return;

      function clearClones() {
        track.querySelectorAll('[data-scroll-strip-clone]').forEach(function (node) {
          node.remove();
        });
        track.dataset.scrollStripCloned = '';
        viewport.classList.remove('is-animating', 'is-paused');
        track.style.animationPlayState = '';
      }

      var setupRetries = 0;

      function setup() {
        clearClones();
        if (!mq.matches || reduceMotion.matches) {
          setupRetries = 0;
          return;
        }

        var originals = Array.prototype.filter.call(track.children, function (child) {
          return !child.hasAttribute('data-scroll-strip-clone');
        });
        if (!originals.length) return;

        var trackWidth = track.scrollWidth;
        var viewWidth = viewport.clientWidth;
        var forceMarquee = viewport.hasAttribute('data-scroll-strip-force');

        if (trackWidth <= viewWidth + 2 && !forceMarquee) {
          if (setupRetries < 8) {
            setupRetries += 1;
            window.setTimeout(setup, 200);
          }
          return;
        }
        setupRetries = 0;

        originals.forEach(function (item) {
          var clone = item.cloneNode(true);
          clone.setAttribute('data-scroll-strip-clone', 'true');
          clone.setAttribute('aria-hidden', 'true');
          track.appendChild(clone);
        });
        track.dataset.scrollStripCloned = 'true';
        viewport.classList.add('is-animating');
      }

      viewport.addEventListener('touchstart', function () {
        viewport.classList.add('is-paused');
      }, { passive: true });
      viewport.addEventListener('touchend', function () {
        viewport.classList.remove('is-paused');
      }, { passive: true });

      if (typeof mq.addEventListener === 'function') {
        mq.addEventListener('change', setup);
      } else if (typeof mq.addListener === 'function') {
        mq.addListener(setup);
      }
      if (typeof reduceMotion.addEventListener === 'function') {
        reduceMotion.addEventListener('change', setup);
      } else if (typeof reduceMotion.addListener === 'function') {
        reduceMotion.addListener(setup);
      }

      window.addEventListener('resize', setup, { passive: true });
      window.addEventListener('load', setup, { passive: true });

      if (typeof ResizeObserver !== 'undefined') {
        var ro = new ResizeObserver(function () { setup(); });
        ro.observe(viewport);
        ro.observe(track);
      }

      setup();
      window.setTimeout(setup, 150);
      window.setTimeout(setup, 600);
    });
  }

  function initFbtScroll(root) {
    var wrap = root.querySelector('[data-codeskin-fbt-scroll]');
    if (!wrap || wrap.dataset.fbtScrollBound === 'true') return;
    wrap.dataset.fbtScrollBound = 'true';

    var track = wrap.querySelector('.fbt-products');
    var prev = wrap.querySelector('[data-fbt-prev]');
    var next = wrap.querySelector('[data-fbt-next]');
    var pagination = wrap.parentElement
      ? wrap.parentElement.querySelector('[data-fbt-pagination]')
      : null;
    var dots = pagination ? pagination.querySelectorAll('[data-fbt-dot]') : [];
    if (!track) return;

    function isMobFbtPeek() {
      return window.matchMedia('(max-width: 900px)').matches;
    }

    function getCols() {
      return track.querySelectorAll('.fbt-col');
    }

    function getActiveIndex() {
      var cols = getCols();
      if (!cols.length) return 0;

      if (isMobFbtPeek()) {
        var trackRect = track.getBoundingClientRect();
        var center = trackRect.left + trackRect.width / 2;
        var closest = 0;
        var minDist = Infinity;
        cols.forEach(function (col, i) {
          var rect = col.getBoundingClientRect();
          var colCenter = rect.left + rect.width / 2;
          var dist = Math.abs(colCenter - center);
          if (dist < minDist) {
            minDist = dist;
            closest = i;
          }
        });
        return closest;
      }

      var scrollLeft = track.scrollLeft;
      var closest = 0;
      var minDist = Infinity;
      cols.forEach(function (col, i) {
        var dist = Math.abs(col.offsetLeft - scrollLeft);
        if (dist < minDist) {
          minDist = dist;
          closest = i;
        }
      });
      return closest;
    }

    function syncActiveCol() {
      if (!isMobFbtPeek()) return;
      var cols = getCols();
      if (!cols.length) return;
      var active = getActiveIndex();
      cols.forEach(function (col, i) {
        var isActive = i === active;
        var isAdjacent = Math.abs(i - active) === 1;
        col.classList.toggle('is-fbt-active', isActive);
        col.classList.toggle('is-fbt-adjacent', !isActive && isAdjacent);
        col.classList.toggle('is-fbt-away', !isActive && !isAdjacent);
      });
      syncPagination();
    }

    function syncPagination() {
      if (!dots.length) return;
      if (pagination) {
        pagination.hidden = dots.length <= 1;
        pagination.style.display = isMobFbtPeek() && dots.length > 1 ? '' : 'none';
      }
      if (!isMobFbtPeek()) return;
      var active = getActiveIndex();
      dots.forEach(function (dot, i) {
        var on = i === active;
        dot.classList.toggle('is-active', on);
        dot.setAttribute('aria-selected', on ? 'true' : 'false');
      });
    }

    function syncButtons() {
      var cols = getCols();
      var maxScroll = Math.max(0, track.scrollWidth - track.clientWidth);
      var hasOverflow = maxScroll > 4;

      if (isMobFbtPeek()) {
        var active = getActiveIndex();
        var showNav = cols.length > 1;
        if (prev) {
          prev.disabled = !showNav || active <= 0;
          prev.hidden = !showNav;
        }
        if (next) {
          next.disabled = !showNav || active >= cols.length - 1;
          next.hidden = !showNav;
        }
        syncPagination();
        return;
      }

      if (prev) {
        prev.disabled = !hasOverflow || track.scrollLeft <= 2;
        prev.hidden = !hasOverflow;
      }
      if (next) {
        next.disabled = !hasOverflow || track.scrollLeft >= maxScroll - 2;
        next.hidden = !hasOverflow;
      }
      if (!hasOverflow && cols.length) return;
    }

    function scrollColToCenter(col) {
      if (!col || !track) return;
      var trackRect = track.getBoundingClientRect();
      var colRect = col.getBoundingClientRect();
      var delta = colRect.left + colRect.width / 2 - (trackRect.left + trackRect.width / 2);
      var maxScroll = Math.max(0, track.scrollWidth - track.clientWidth);
      track.scrollTo({
        left: Math.max(0, Math.min(track.scrollLeft + delta, maxScroll)),
        behavior: 'smooth'
      });
    }

    function scrollToIndex(index) {
      var cols = getCols();
      if (!cols.length) return;
      var maxIndex = cols.length - 1;
      var target = Math.max(0, Math.min(maxIndex, index));
      if (isMobFbtPeek()) {
        scrollColToCenter(cols[target]);
        window.setTimeout(syncActiveCol, 80);
        window.setTimeout(syncActiveCol, 320);
        window.setTimeout(syncPagination, 320);
        return;
      }
      track.scrollTo({ left: cols[target].offsetLeft, behavior: 'smooth' });
    }

    function scrollBy(dir) {
      scrollToIndex(getActiveIndex() + dir);
    }

    if (prev) prev.addEventListener('click', function () { scrollBy(-1); });
    if (next) next.addEventListener('click', function () { scrollBy(1); });
    dots.forEach(function (dot) {
      dot.addEventListener('click', function () {
        var idx = parseInt(dot.getAttribute('data-fbt-dot'), 10);
        if (!isNaN(idx)) scrollToIndex(idx);
      });
    });
    track.addEventListener(
      'scroll',
      function () {
        syncButtons();
        syncActiveCol();
      },
      { passive: true }
    );
    window.addEventListener(
      'resize',
      function () {
        syncButtons();
        syncActiveCol();
      },
      { passive: true }
    );
    syncButtons();
    syncActiveCol();
    if (isMobFbtPeek()) {
      var cols = getCols();
      if (cols.length) {
        requestAnimationFrame(function () {
          scrollColToCenter(cols[0]);
          requestAnimationFrame(function () {
            syncActiveCol();
            syncButtons();
          });
        });
      }
    }
    window.setTimeout(function () {
      syncButtons();
      syncActiveCol();
      if (isMobFbtPeek()) {
        var cols = getCols();
        if (cols.length) scrollColToCenter(cols[getActiveIndex()]);
      }
    }, 200);
  }

  function initFbt(root) {
    initFbtScroll(root);
    var fbt = root.querySelector('[data-codeskin-fbt]');
    if (!fbt) return;
    var btn = fbt.querySelector('[data-codeskin-fbt-atc]');
    if (!btn || btn.dataset.fbtBound === 'true') return;
    btn.dataset.fbtBound = 'true';

    var raw = fbt.getAttribute('data-fbt-variants') || '';
    var ids = raw.split(',').map(function (s) {
      return parseInt(s.trim(), 10);
    }).filter(function (n) {
      return !isNaN(n) && n > 0;
    });
    if (!ids.length) {
      btn.disabled = true;
      return;
    }

    if (!btn.getAttribute('data-codeskin-add-label')) {
      btn.setAttribute('data-codeskin-add-label', btn.textContent.trim());
    }

    btn.addEventListener('click', function () {
      if (btn.classList.contains('is-added')) return;
      btn.disabled = true;
      btn.classList.add('is-adding');
      var defaultLabel = btn.textContent.trim();
      btn.textContent = 'Adding…';

      if (window.codeskinCart && typeof window.codeskinCart.addMultipleVariants === 'function') {
        window.codeskinCart
          .addMultipleVariants(ids)
          .then(function (cart) {
            if (!cart) {
              btn.disabled = false;
              btn.classList.remove('is-adding');
              btn.textContent = defaultLabel;
            }
          });
        return;
      }

      fetch(window.Shopify && window.Shopify.routes ? window.Shopify.routes.root + 'cart/add.js' : '/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({
          items: ids.map(function (id) {
            return { id: id, quantity: 1 };
          }),
        }),
      })
        .then(function (res) {
          return res.json().then(function (data) {
            if (!res.ok) throw data;
            return data;
          });
        })
        .then(function () {
          document.dispatchEvent(new CustomEvent('cart:refresh', { bubbles: true }));
          if (typeof window.showCodeskinCartToast === 'function') {
            window.showCodeskinCartToast('Bundle added to your bag', 'success');
          }
          if (window.codeskinCart && typeof window.codeskinCart.syncAddedFromCart === 'function') {
            window.codeskinCart.syncAddedFromCart();
          }
        })
        .catch(function () {
          btn.disabled = false;
          btn.classList.remove('is-adding');
          btn.textContent = defaultLabel;
        });
    });
  }

  function initMfcScrolls(scope) {
    var root = scope || document;
    root.querySelectorAll('[data-codeskin-mfc-scroll]').forEach(function (wrap) {
      if (wrap.dataset.mfcScrollBound === 'true') return;
      wrap.dataset.mfcScrollBound = 'true';

      var track = wrap.querySelector('.mfc-track');
      var prev = wrap.querySelector('[data-mfc-prev]');
      var next = wrap.querySelector('[data-mfc-next]');
      var inner = wrap.closest('.mfc-inner');
      var pagination = inner ? inner.querySelector('[data-mfc-pagination]') : null;
      var dots = pagination ? pagination.querySelectorAll('[data-mfc-dot]') : [];
      if (!track) return;

      function isMobMfcPeek() {
        return window.matchMedia('(max-width: 900px)').matches;
      }

      function getCards() {
        return track.querySelectorAll('.mfc-card');
      }

      function getActiveIndex() {
        var cards = getCards();
        if (!cards.length) return 0;

        if (isMobMfcPeek()) {
          var trackRect = track.getBoundingClientRect();
          var center = trackRect.left + trackRect.width / 2;
          var closest = 0;
          var minDist = Infinity;
          cards.forEach(function (card, i) {
            var rect = card.getBoundingClientRect();
            var cardCenter = rect.left + rect.width / 2;
            var dist = Math.abs(cardCenter - center);
            if (dist < minDist) {
              minDist = dist;
              closest = i;
            }
          });
          return closest;
        }

        var scrollLeft = track.scrollLeft;
        var closest = 0;
        var minDist = Infinity;
        cards.forEach(function (card, i) {
          var dist = Math.abs(card.offsetLeft - scrollLeft);
          if (dist < minDist) {
            minDist = dist;
            closest = i;
          }
        });
        return closest;
      }

      function syncPagination() {
        if (!dots.length) return;
        if (pagination) {
          pagination.hidden = dots.length <= 1;
          pagination.style.display = isMobMfcPeek() && dots.length > 1 ? '' : 'none';
        }
        if (!isMobMfcPeek()) return;
        var active = getActiveIndex();
        dots.forEach(function (dot, i) {
          var on = i === active;
          dot.classList.toggle('is-active', on);
          dot.setAttribute('aria-selected', on ? 'true' : 'false');
        });
      }

      function syncActiveCard() {
        if (!isMobMfcPeek()) return;
        var cards = getCards();
        if (!cards.length) return;
        var active = getActiveIndex();
        cards.forEach(function (card, i) {
          var isActive = i === active;
          var isAdjacent = Math.abs(i - active) === 1;
          card.classList.toggle('is-mfc-active', isActive);
          card.classList.toggle('is-mfc-adjacent', !isActive && isAdjacent);
          card.classList.toggle('is-mfc-away', !isActive && !isAdjacent);
        });
        syncPagination();
      }

      function syncButtons() {
        var cards = getCards();
        var maxScroll = Math.max(0, track.scrollWidth - track.clientWidth);
        var hasOverflow = maxScroll > 4;

        if (isMobMfcPeek()) {
          var active = getActiveIndex();
          var showNav = cards.length > 1;
          if (prev) {
            prev.hidden = !showNav;
            prev.disabled = !showNav || active <= 0;
          }
          if (next) {
            next.hidden = !showNav;
            next.disabled = !showNav || active >= cards.length - 1;
          }
          syncPagination();
          return;
        }

        if (prev) {
          prev.disabled = !hasOverflow || track.scrollLeft <= 2;
          prev.hidden = !hasOverflow;
        }
        if (next) {
          next.disabled = !hasOverflow || track.scrollLeft >= maxScroll - 2;
          next.hidden = !hasOverflow;
        }
      }

      function scrollCardToCenter(card) {
        if (!card || !track) return;
        var trackRect = track.getBoundingClientRect();
        var cardRect = card.getBoundingClientRect();
        var delta = cardRect.left + cardRect.width / 2 - (trackRect.left + trackRect.width / 2);
        var maxScroll = Math.max(0, track.scrollWidth - track.clientWidth);
        track.scrollTo({
          left: Math.max(0, Math.min(track.scrollLeft + delta, maxScroll)),
          behavior: 'smooth'
        });
      }

      function scrollToIndex(index) {
        var cards = getCards();
        if (!cards.length) return;
        var maxIndex = cards.length - 1;
        var target = Math.max(0, Math.min(maxIndex, index));
        if (isMobMfcPeek()) {
          scrollCardToCenter(cards[target]);
          window.setTimeout(syncActiveCard, 80);
          window.setTimeout(syncActiveCard, 320);
          window.setTimeout(syncPagination, 320);
          return;
        }
        track.scrollTo({ left: cards[target].offsetLeft, behavior: 'smooth' });
      }

      function scrollBy(dir) {
        if (isMobMfcPeek()) {
          scrollToIndex(getActiveIndex() + dir);
          return;
        }
        track.scrollBy({ left: dir * track.offsetWidth * 0.75, behavior: 'smooth' });
      }

      if (prev) prev.addEventListener('click', function () { scrollBy(-1); });
      if (next) next.addEventListener('click', function () { scrollBy(1); });
      dots.forEach(function (dot) {
        dot.addEventListener('click', function () {
          var idx = parseInt(dot.getAttribute('data-mfc-dot'), 10);
          if (!isNaN(idx)) scrollToIndex(idx);
        });
      });
      track.addEventListener(
        'scroll',
        function () {
          syncButtons();
          syncActiveCard();
        },
        { passive: true }
      );
      window.addEventListener(
        'resize',
        function () {
          syncButtons();
          syncActiveCard();
        },
        { passive: true }
      );
      syncButtons();
      syncActiveCard();
      if (isMobMfcPeek()) {
        var cards = getCards();
        if (cards.length) {
          requestAnimationFrame(function () {
            scrollCardToCenter(cards[0]);
            requestAnimationFrame(function () {
              syncActiveCard();
              syncButtons();
            });
          });
        }
      }
      window.setTimeout(function () {
        syncButtons();
        syncActiveCard();
        if (isMobMfcPeek()) {
          var cards = getCards();
          if (cards.length) scrollCardToCenter(cards[getActiveIndex()]);
        }
      }, 200);
    });
  }

  function initVtVideos(root) {
    root.querySelectorAll('.vt-pdp-video').forEach(function (video) {
      var card = video.closest('.vt-pdp-card');
      if (!card || video.dataset.vtBound === 'true') return;
      video.dataset.vtBound = 'true';
      card.addEventListener('mouseenter', function () {
        video.play().catch(function () {});
      });
      card.addEventListener('mouseleave', function () {
        video.pause();
        video.currentTime = 0;
      });
    });
  }

  function initReviews(root) {
    var preview =
      root.querySelector('[data-codeskin-rev-preview]') ||
      root.querySelector('#codeskin-pdp-rev-preview');
    if (!preview) return;

    bootRevCarousel(preview);

    var observer = new MutationObserver(function () {
      bootRevCarousel(preview);
    });
    observer.observe(preview, { childList: true, subtree: true });
  }

  function getRevTrack(preview) {
    return (
      preview.querySelector('.jdgm-rev-widg__reviews') ||
      preview.querySelector('.jdgm-rev-widg__body')
    );
  }

  function stopRevCarousel(preview) {
    if (preview._revCarouselRaf) {
      cancelAnimationFrame(preview._revCarouselRaf);
      preview._revCarouselRaf = null;
    }
  }

  function bootRevCarousel(preview) {
    if (preview.classList.contains('is-expanded')) return;

    var customGrid = preview.querySelector('[data-codeskin-rev-grid]');
    if (customGrid && customGrid.children.length > 0) {
      stopRevCarousel(preview);
      return;
    }

    var track = getRevTrack(preview);
    if (!track) return;

    var revs = track.querySelectorAll(':scope > .jdgm-rev:not([data-codeskin-rev-clone])');
    if (revs.length < 2) return;

    if (!track.dataset.codeskinCarouselReady) {
      track.dataset.codeskinCarouselReady = 'true';
      revs.forEach(function (rev) {
        var clone = rev.cloneNode(true);
        clone.setAttribute('data-codeskin-rev-clone', 'true');
        clone.setAttribute('aria-hidden', 'true');
        track.appendChild(clone);
      });
    }

    if (preview._revCarouselRaf) return;

    if (!preview.dataset.revCarouselListeners) {
      preview.dataset.revCarouselListeners = 'true';
      preview._revCarouselPaused = false;
      preview.addEventListener('mouseenter', function () {
        preview._revCarouselPaused = true;
      });
      preview.addEventListener('mouseleave', function () {
        preview._revCarouselPaused = false;
      });
      preview.addEventListener('focusin', function () {
        preview._revCarouselPaused = true;
      });
      preview.addEventListener('focusout', function () {
        preview._revCarouselPaused = false;
      });
    }

    function tick() {
      if (
        !preview.classList.contains('is-expanded') &&
        !preview._revCarouselPaused &&
        track.scrollWidth > track.clientWidth
      ) {
        track.scrollLeft += 0.6;
        var loopAt = track.scrollWidth / 2;
        if (track.scrollLeft >= loopAt) {
          track.scrollLeft -= loopAt;
        }
      }
      preview._revCarouselRaf = requestAnimationFrame(tick);
    }

    preview._revCarouselRaf = requestAnimationFrame(tick);
  }

  function initSectionNav(root) {
    var nav = root.querySelector('[data-codeskin-section-nav]');
    if (!nav || nav.dataset.navBound === 'true') return;
    nav.dataset.navBound = 'true';

    var links = nav.querySelectorAll('[data-codeskin-nav-link]');
    links.forEach(function (link) {
      var id = (link.getAttribute('href') || '').replace('#', '');
      if (!id || !document.getElementById(id)) {
        link.hidden = true;
      }
    });

    var visible = Array.prototype.filter.call(links, function (l) {
      return !l.hidden;
    });
    if (!visible.length) {
      nav.hidden = true;
      return;
    }

    function setActive(id) {
      visible.forEach(function (link) {
        var targetId = (link.getAttribute('href') || '').replace('#', '');
        link.classList.toggle('active', targetId === id);
      });
    }

    visible.forEach(function (link) {
      link.addEventListener('click', function (e) {
        var id = (link.getAttribute('href') || '').replace('#', '');
        var el = document.getElementById(id);
        if (!el) return;
        e.preventDefault();
        var top = el.getBoundingClientRect().top + window.scrollY - 120;
        window.scrollTo({ top: top, behavior: 'smooth' });
        setActive(id);
      });
    });

    if ('IntersectionObserver' in window) {
      var sections = visible
        .map(function (link) {
          var id = (link.getAttribute('href') || '').replace('#', '');
          return document.getElementById(id);
        })
        .filter(Boolean);

      var observer = new IntersectionObserver(
        function (entries) {
          entries.forEach(function (entry) {
            if (entry.isIntersecting) {
              setActive(entry.target.id);
            }
          });
        },
        { rootMargin: '-30% 0px -55% 0px', threshold: 0 }
      );

      sections.forEach(function (section) {
        observer.observe(section);
      });
    }
  }

  function formatPincodeLocation(location) {
    if (!location) return '';
    var parts = [];
    if (location.area) parts.push(location.area);
    if (location.district && location.district.toLowerCase() !== (location.area || '').toLowerCase()) {
      parts.push(location.district);
    }
    if (location.state) parts.push(location.state);
    return parts.join(', ');
  }

  function buildDeliveryMessage(pin, location, shippingLabel) {
    var place = formatPincodeLocation(location);
    var lead = place
      ? 'We deliver to ' + place + ' (PIN ' + pin + ')'
      : 'We deliver to PIN ' + pin + ' across India';
    if (shippingLabel) {
      lead += ' — ' + shippingLabel;
    }
    return lead + '. Dispatches in 24 hours from our Mumbai warehouse.';
  }

  function fetchIndiaPincodeLocation(pin) {
    return fetch('https://api.postalpincode.in/pincode/' + encodeURIComponent(pin), {
      headers: { Accept: 'application/json' },
    })
      .then(function (res) {
        return res.json();
      })
      .then(function (data) {
        if (!Array.isArray(data) || !data[0]) return null;
        var block = data[0];
        if (block.Status !== 'Success' || !block.PostOffice || !block.PostOffice.length) {
          return null;
        }
        var office = block.PostOffice[0];
        return {
          area: office.Name || '',
          district: office.District || '',
          state: office.State || '',
        };
      })
      .catch(function () {
        return null;
      });
  }

  function fetchShippingRates(pin) {
    var cartUrl = (window.routes && window.routes.cart_url) || '/cart';
    var url = cartUrl.replace(/\/$/, '') + '/shipping_rates.json';

    return fetch(url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({
        shipping_address: {
          zip: pin,
          country: 'IN',
        },
      }),
    })
      .then(function (res) {
        return res.json();
      })
      .then(function (data) {
        var rates = data && data.shipping_rates;
        if (!rates || !rates.length) return '';
        var rate = rates[0];
        return rate.presentment_name || rate.name || '';
      })
      .catch(function () {
        return '';
      });
  }

  function isValidIndiaPincode(pin) {
    return /^[1-9]\d{5}$/.test(pin);
  }

  function getIndiaPincodeError(pin) {
    if (!pin) {
      return 'Please enter your 6-digit PIN code.';
    }
    if (pin.length < 6) {
      return 'PIN code must be exactly 6 digits.';
    }
    if (pin.length > 6) {
      return 'PIN code must be exactly 6 digits.';
    }
    if (pin.charAt(0) === '0') {
      return 'Indian PIN codes cannot start with 0. Enter a valid 6-digit PIN.';
    }
    if (!/^\d{6}$/.test(pin)) {
      return 'PIN code must contain only numbers.';
    }
    return 'Please enter a valid 6-digit Indian PIN code.';
  }

  function initPincode(root) {
    var form = root.querySelector('[data-codeskin-pincode]');
    if (!form || form.dataset.pinBound === 'true') return;
    form.dataset.pinBound = 'true';

    var input = form.querySelector('[data-codeskin-pincode-input]');
    var checkBtn = form.querySelector('[data-codeskin-pincode-check]');
    var msg = root.querySelector('[data-codeskin-pincode-msg]');
    if (!input || !checkBtn || !msg) return;

    function showMsg(text, isError) {
      msg.hidden = false;
      msg.textContent = text;
      msg.classList.toggle('is-error', !!isError);
    }

    input.addEventListener('input', function () {
      var digits = (input.value || '').replace(/\D/g, '').slice(0, 6);
      if (input.value !== digits) input.value = digits;
      if (msg.hidden === false && digits.length < 6) {
        msg.hidden = true;
        msg.textContent = '';
        msg.classList.remove('is-error');
      }
    });

    function setChecking(isChecking) {
      checkBtn.disabled = isChecking;
      checkBtn.setAttribute('aria-busy', isChecking ? 'true' : 'false');
      if (isChecking) {
        if (!checkBtn.dataset.defaultHtml) {
          checkBtn.dataset.defaultHtml = checkBtn.innerHTML;
        }
        checkBtn.textContent = 'Checking…';
      } else if (checkBtn.dataset.defaultHtml) {
        checkBtn.innerHTML = checkBtn.dataset.defaultHtml;
      }
    }

    function showDeliverySuccess(pin, location, shippingLabel) {
      showMsg(buildDeliveryMessage(pin, location, shippingLabel), false);
    }

    function runPincodeCheck() {
      var pin = (input.value || '').replace(/\D/g, '');
      if (!isValidIndiaPincode(pin)) {
        showMsg(getIndiaPincodeError(pin), true);
        input.focus();
        return;
      }

      setChecking(true);

      Promise.all([fetchIndiaPincodeLocation(pin), fetchShippingRates(pin)])
        .then(function (results) {
          var location = results[0];
          var shippingLabel = results[1];
          showDeliverySuccess(pin, location, shippingLabel);
        })
        .catch(function () {
          showDeliverySuccess(pin, null, '');
        })
        .finally(function () {
          setChecking(false);
        });
    }

    checkBtn.addEventListener('click', runPincodeCheck);
    form.addEventListener('submit', function (event) {
      event.preventDefault();
      runPincodeCheck();
    });
    input.addEventListener('keydown', function (event) {
      if (event.key === 'Enter') {
        event.preventDefault();
        runPincodeCheck();
      }
    });
  }

  function initTrustTaps(root) {
    var wrap = root.querySelector('[data-codeskin-trust]');
    if (!wrap || wrap.dataset.trustBound === 'true') return;
    wrap.dataset.trustBound = 'true';

    var panel = wrap.querySelector('[data-codeskin-trust-panel]');
    var triggers = wrap.querySelectorAll('[data-codeskin-trust-trigger]');
    if (!panel || !triggers.length) return;

    var mq = window.matchMedia('(max-width: 900px)');

    function isMobile() {
      return mq.matches;
    }

    function closePanel() {
      panel.hidden = true;
      panel.innerHTML = '';
      triggers.forEach(function (btn) {
        btn.setAttribute('aria-expanded', 'false');
        btn.classList.remove('is-active');
      });
    }

    function openPanel(btn) {
      var targetId = btn.getAttribute('data-trust-target');
      var src = targetId ? document.getElementById(targetId) : null;
      if (!src) return;

      triggers.forEach(function (b) {
        b.setAttribute('aria-expanded', 'false');
        b.classList.remove('is-active');
      });
      btn.setAttribute('aria-expanded', 'true');
      btn.classList.add('is-active');
      panel.innerHTML = src.innerHTML;
      panel.hidden = false;
    }

    triggers.forEach(function (btn) {
      btn.addEventListener('click', function () {
        if (!isMobile()) return;

        var isOpen = btn.getAttribute('aria-expanded') === 'true';
        if (isOpen) {
          closePanel();
          return;
        }
        openPanel(btn);
      });
    });

    if (typeof mq.addEventListener === 'function') {
      mq.addEventListener('change', function () {
        if (!isMobile()) closePanel();
      });
    } else if (typeof mq.addListener === 'function') {
      mq.addListener(function () {
        if (!isMobile()) closePanel();
      });
    }
  }

  function boot() {
    document.querySelectorAll('.codeskin-pdp[data-codeskin-pdp]').forEach(initPdp);
    initMfcScrolls(document);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }

  document.addEventListener('shopify:section:load', function (e) {
    var pdp = e.target.querySelector('.codeskin-pdp[data-codeskin-pdp]');
    if (pdp) initPdp(pdp);
    initMfcScrolls(e.target);
  });
})();
